import {
  normalizeHeartRefillPrice,
  StatisticsStorage
} from '../storage/statistics_storage.js';
import { getPlayer, updatePlayer } from './requests/player.requests.js';
import { AuthSessionStore } from './auth_session.js';
import type { PlayerDto, UpdatePlayerRequest } from './dto/player.dto.js';
import type { GameStatistics } from "../storage/statistics_storage.js";
import { setCampaignAnalyticsFromPlayer } from '../analytics/campaign_analytics.js';
import { CumulativeBoosterService } from '../cumulative_boosters/cumulative_booster_service.js';
import { GlobalSettings } from '../global_settings.js';
import { OfflineJournal } from '../offline/offline_journal.js';
import { NetworkManager } from '../network_manager.js';
import { PLAYER_CREATED_AT_KEY } from '../common/days_in_game.js';

const SYNC_OWNER_KEY = 'player_sync_owner';

interface SyncOwner
{
  api_url: string;
  player_id: number | null;
}

function readSyncOwner(): SyncOwner | null
{
  try
  {
    const raw = localStorage.getItem( SYNC_OWNER_KEY );
    if ( !raw ) return null;
    const parsed = JSON.parse( raw ) as Partial<SyncOwner>;
    return typeof parsed.api_url === 'string'
      ? { api_url: parsed.api_url, player_id: typeof parsed.player_id === 'number' ? parsed.player_id : null }
      : null;
  }
  catch
  {
    return null;
  }
}

function writeSyncOwner( owner: SyncOwner ): void
{
  try { localStorage.setItem( SYNC_OWNER_KEY, JSON.stringify( owner ) ); }
  catch {}
}

// Local progress is only ever merged into the account it was last synced
// with. After a server or account switch it is replaced by the server copy,
// so that seen levels and tutorials from one account never leak into another.
// Installs from before the stamp existed carry no owner and are trusted.
export function isLocalProgressOwnedByCurrentAccount(): boolean
{
  const owner = readSyncOwner();
  if ( !owner ) return true;
  return owner.api_url === GlobalSettings.API_BASE_URL
    && owner.player_id === AuthSessionStore.getPlayerId();
}

/** Server ISO deadline → local ms; an absent or already-passed one is 0. */
export function serverUntilMs( value: string | null | undefined ): number
{
  if ( !value )
    return 0;
  const ms = Date.parse( value );
  return Number.isFinite( ms ) && ms > Date.now() ? ms : 0;
}

export function infiniteHeartsUntilMs( value: string | null | undefined ): number
{
  return serverUntilMs( value );
}

export function nextHeartAtMs( value: string | null | undefined, fallback: number ): number
{
  if ( value === undefined ) return fallback;
  if ( value === null ) return 0;
  const ms = Date.parse( value );
  return Number.isFinite( ms ) ? ms : fallback;
}

export function localToServer( local: GameStatistics ): UpdatePlayerRequest
{
  return {
    last_seen_level: Math.max( 1, local.seen_level ),
  };
}

function mergeCompletedBoosterTutorials( server: PlayerDto, local: GameStatistics ): number[]
{
  return Array.from( new Set( [
    ...( server.completed_booster_tutorials ?? [] ),
    ...local.completed_booster_tutorials,
  ] ) );
}

function mergeCompletedFeatureTutorials( server: PlayerDto, local: GameStatistics ): number[]
{
  return Array.from( new Set( [
    ...( server.completed_feature_tutorials ?? [] ),
    ...local.completed_feature_tutorials,
  ] ) );
}

function serverToLocal( server: PlayerDto, merge_local = true ): GameStatistics
{
  const local = StatisticsStorage.load();
  return {
    level:          Math.max( 1, server.player_level ?? local.level ),
    seen_level:     Math.max( 1, server.last_seen_level ?? local.seen_level ),
    rewarded_levels: server.rewarded_levels ?? [],
    adventure_rewards_collected: local.adventure_rewards_collected,
    adventure_intro_scroll_played: local.adventure_intro_scroll_played,
    adventure_intro_scroll_period: local.adventure_intro_scroll_period,
    points:         server.points ?? 0,
    coins:          server.coins ?? 0,
    hearts:         server.hearts ?? local.hearts,
    max_hearts:     server.max_hearts ?? local.max_hearts,
    next_heart_at:  nextHeartAtMs( server.next_heart_at, local.next_heart_at ),
    infinite_hearts_until: infiniteHeartsUntilMs( server.infinite_hearts_until ),
    no_ads_until:   serverUntilMs( server.no_ads_until ),
    heart_refill_price: normalizeHeartRefillPrice( server.heart_refill_price ),
    fireworks:      server.fireworks,
    multiballs:     server.multi_balls,
    big_balls:      server.big_balls,
    infinite_balls: server.infinite_balls,
    fireworks_price: server.fireworks_price ?? local.fireworks_price,
    fireworks_pack_qty: server.fireworks_pack_qty ?? local.fireworks_pack_qty,
    music_enabled:  local.music_enabled,
    sounds_enabled: local.sounds_enabled,
    haptics_enabled: local.haptics_enabled,
    haptics_strength: local.haptics_strength,
    install_date:   local.install_date,
    rate_dialog_show_count: local.rate_dialog_show_count,
    rate_dialog_last_shown_level: local.rate_dialog_last_shown_level,
    rate_dialog_completed: local.rate_dialog_completed,
    tutorial_completed: local.tutorial_completed,
    big_ball_tutorial_completed: local.big_ball_tutorial_completed,
    completed_booster_tutorials: merge_local ? mergeCompletedBoosterTutorials( server, local ) : [ ...( server.completed_booster_tutorials ?? [] ) ],
    completed_feature_tutorials: merge_local ? mergeCompletedFeatureTutorials( server, local ) : [ ...( server.completed_feature_tutorials ?? [] ) ],
    new_goal_pending: local.new_goal_pending,
    last_menu_level: local.last_menu_level,
    daily_challenge_completed_days: local.daily_challenge_completed_days,
    daily_challenge_seen_completed_days: local.daily_challenge_seen_completed_days,
  };
}

export function loadPlayerFromServer( server: PlayerDto ): void
{
  syncProfileFromServer( server );
  StatisticsStorage.save( serverToLocal( server ) );
}

export function adoptEconomyFromPlayer( server: PlayerDto ): void
{
  CumulativeBoosterService.applyInventory( server );

  const patch: Partial<GameStatistics> = {
    level:           Math.max( 1, server.player_level ?? 1 ),
    points:          server.points ?? 0,
    coins:           server.coins ?? 0,
    hearts:          server.hearts ?? 0,
    rewarded_levels: server.rewarded_levels ?? [],
  };

  if ( server.max_hearts !== undefined )
    patch.max_hearts = server.max_hearts;

  if ( server.next_heart_at !== undefined )
    patch.next_heart_at = nextHeartAtMs( server.next_heart_at, 0 );

  if ( server.infinite_hearts_until !== undefined )
    patch.infinite_hearts_until = infiniteHeartsUntilMs( server.infinite_hearts_until );

  if ( server.no_ads_until !== undefined )
    patch.no_ads_until = serverUntilMs( server.no_ads_until );

  patch.heart_refill_price = normalizeHeartRefillPrice( server.heart_refill_price );

  if ( server.completed_booster_tutorials !== undefined )
    patch.completed_booster_tutorials = mergeCompletedBoosterTutorials( server, StatisticsStorage.load() );

  if ( server.completed_feature_tutorials !== undefined )
    patch.completed_feature_tutorials = mergeCompletedFeatureTutorials( server, StatisticsStorage.load() );

  patch.fireworks = server.fireworks;
  patch.multiballs = server.multi_balls;
  patch.big_balls = server.big_balls;
  patch.infinite_balls = server.infinite_balls;

  if ( server.fireworks_price !== undefined )
    patch.fireworks_price = server.fireworks_price;

  if ( server.fireworks_pack_qty !== undefined )
    patch.fireworks_pack_qty = server.fireworks_pack_qty;

  StatisticsStorage.update( patch );
}

let refresh_inflight: Promise<void> | null = null;

export function refreshPlayerFromServer(): Promise<void>
{
  if ( !AuthSessionStore.isLoggedIn() || !NetworkManager.isOnline() )
    return Promise.resolve();

  if ( OfflineJournal.hasPending() )
    return import( "../offline/offline_sync_service.js" ).then( m => m.OfflineSyncService.sync( "player_refresh" ) );

  if ( refresh_inflight !== null )
    return refresh_inflight;

  refresh_inflight = ( async () =>
  {
    try
    {
      const player = await getPlayer();
      syncProfileFromServer( player );
      adoptEconomyFromPlayer( player );
    }
    catch ( e )
    {
      console.warn( '[PlayerSync] refresh failed', e );
    }
    finally
    {
      refresh_inflight = null;
    }
  } )();

  return refresh_inflight;
}

export function syncProfileFromServer( server: PlayerDto ): void
{
  setCampaignAnalyticsFromPlayer( server );

  // Authoritative: always reflect the currently logged-in account so that
  // switching to an account with no nickname/avatar clears the previous one
  // instead of leaving stale values behind.
  if ( server.nickname )
    localStorage.setItem( 'player_nickname', server.nickname );
  else
    localStorage.removeItem( 'player_nickname' );

  localStorage.setItem( 'player_avatar_id', String( server.avatar_id ?? 0 ) );

  // Facebook avatar URL is set server-side on FB connect (it also sets avatar_id=987).
  // Mirror it into localStorage so the profile/leaderboard can render the photo.
  if ( server.facebook_avatar_url )
    localStorage.setItem( 'player_facebook_avatar_url', server.facebook_avatar_url );
  else
    localStorage.removeItem( 'player_facebook_avatar_url' );

  if ( server.country )
    localStorage.setItem( 'player_country', server.country );

  // "Days in game" counts from the account, not from this device's install.
  if ( server.created_at )
    localStorage.setItem( PLAYER_CREATED_AT_KEY, server.created_at );
}

export async function syncPlayerAfterLogin( server: PlayerDto ): Promise<void>
{
  syncProfileFromServer( server );
  CumulativeBoosterService.initializeFromPlayer( server );

  const local = StatisticsStorage.load();
  const local_seen = Math.max( 1, local.seen_level );
  const owned = isLocalProgressOwnedByCurrentAccount();

  StatisticsStorage.save( serverToLocal( server, owned ) );
  writeSyncOwner( { api_url: GlobalSettings.API_BASE_URL, player_id: AuthSessionStore.getPlayerId() } );
  console.log( `[PlayerSync] Adopted server state (lvl ${server.player_level}, coins ${server.coins}, hearts ${server.hearts})` );

  if ( !owned )
  {
    console.info( '[PlayerSync] Local progress belonged to another account or server, replaced by server copy' );
    return;
  }

  if ( local_seen > ( server.last_seen_level ?? 1 ) )
  {
    try
    {
      await updatePlayer( { last_seen_level: local_seen } );
    }
    catch ( e )
    {
      console.warn( '[PlayerSync] Failed to push last_seen_level', e );
    }
  }

  const server_tutorials = new Set( server.completed_booster_tutorials ?? [] );
  const merged_tutorials = mergeCompletedBoosterTutorials( server, local );
  if ( merged_tutorials.some( id => !server_tutorials.has( id ) ) )
  {
    try
    {
      await updatePlayer( { completed_booster_tutorials: merged_tutorials } );
    }
    catch ( e )
    {
      console.warn( '[PlayerSync] Failed to push completed_booster_tutorials', e );
    }
  }

  const server_feature_tutorials = new Set( server.completed_feature_tutorials ?? [] );
  const merged_feature_tutorials = mergeCompletedFeatureTutorials( server, local );
  if ( merged_feature_tutorials.some( id => !server_feature_tutorials.has( id ) ) )
  {
    try
    {
      await updatePlayer( { completed_feature_tutorials: merged_feature_tutorials } );
    }
    catch ( e )
    {
      console.warn( '[PlayerSync] Failed to push completed_feature_tutorials', e );
    }
  }
}
